import { useRoundHref } from "@client/RoundRouteContext";
import { trpc } from "@client/trpc";
import { type RoundVisitRow } from "@lib/todoRailTypes";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@sfc/ui";
import {
  type Dispatch,
  type SetStateAction,
  useCallback,
  useMemo,
  useState,
} from "react";
import {
  funderDecidePath,
  funderReviewPath,
  normalizeFunderDecideSubview,
} from "../useFunderWorkspaceRouteState";
import { meetingDeepLink } from "../queue/queueDeepLinks";

import { TodoDrawerBody } from "./TodoDrawerBody";
import {
  TodoHistoryList,
  visitKindLabels,
  type HistoryTarget,
} from "./TodoHistoryList";
import { TodoProgressRing } from "./TodoProgressRing";
import { TodoResumeCard } from "./TodoResumeCard";
import { flattenTodoSteps } from "@lib/todoRail/todoRailModel";
import { useTodoRail } from "./useTodoRail";
import { useRound } from "../../round-context";
import {
  getActiveProposals,
  getActiveRecommenders,
} from "@engine/round/accessors";

/**
 * The drawer's two tabs.
 *
 * They answer different questions and so carry different cards, which is why
 * splitting them was worth doing: **To Do** is the plan ("what should I do
 * next?") and leads with the next unfinished step; **History** is the record
 * ("where was I?") and leads with the most recent place. Before the split one
 * card tried to be both and flipped its own meaning depending on state.
 */
export function TodoDrawerContent({
  roundId,
  onNavigate,
  expanded,
  setExpanded,
}: {
  roundId: string;
  onNavigate: (href: string) => void;
  expanded: Record<string, boolean>;
  setExpanded: Dispatch<SetStateAction<Record<string, boolean>>>;
}) {
  const round = useRound();
  const { rail, isLoading } = useTodoRail(roundId, true);
  const videos = trpc.getVideos.useQuery({ roundId }, { staleTime: 60_000 });
  // History has NO impersonation discriminator: the server scopes it to the
  // real user, so viewing as one funder or another cannot change the answer,
  // and one key per round means there are no sibling entries to keep in step.
  const history = trpc.getRoundVisits.useQuery({ roundId });

  // Sampled once per drawer open rather than ticked: "12 min ago" does not
  // need to become "13 min ago" under the reader's eyes, and a live clock here
  // would re-render the whole list every minute for no decision.
  const [now] = useState(() => Date.now());

  // Null, not an empty array: "you have been nowhere" is a fact about the
  // funder's round and must come from a read that happened.
  const visits = useMemo(() => history.data?.visits ?? null, [history.data]);

  // History links are rebuilt from (kind, id) at render, never stored — so a
  // route change can't strand old rows, and a target that has since been
  // deleted degrades to an unclickable entry instead of a dead link.
  // What the round still HAS. The workspace falls back to its first active
  // item when a selection doesn't resolve, so a removed recommender or a
  // withdrawn proposal would silently open somebody else — worse than saying
  // the entry is gone.
  const activeRecommenderIds = useMemo(
    () => new Set(getActiveRecommenders(round).map((r) => r.id)),
    [round],
  );
  const activeProposalIds = useMemo(
    () => new Set(getActiveProposals(round).map((p) => p.id)),
    [round],
  );

  const roundHref = useRoundHref();
  const resolveTarget = useCallback(
    (visit: RoundVisitRow): HistoryTarget => {
      const ready = (href: string): HistoryTarget => ({
        state: "ready",
        href: roundHref(href),
      });

      switch (visit.kind) {
        case "recommender":
          return activeRecommenderIds.has(visit.targetId)
            ? ready(funderReviewPath(roundId, "recs", visit.targetId))
            : { state: "gone" };
        case "proposal":
          return activeProposalIds.has(visit.targetId)
            ? ready(funderReviewPath(roundId, "orgs", visit.targetId))
            : { state: "gone" };
        case "decide": {
          // Validated, not cast: `targetId` is any stored string, so a subview
          // written by a newer build (or since removed) would otherwise mint a
          // link to a route that doesn't exist.
          const decideSubview = normalizeFunderDecideSubview(visit.targetId);
          return decideSubview != null
            ? ready(funderDecidePath(roundId, decideSubview))
            : { state: "gone" };
        }
        case "overview":
          return ready(funderReviewPath(roundId, "overview"));
        case "track":
          return ready(funderReviewPath(roundId, "tracks", visit.targetId));
        case "video": {
          // UNKNOWN, not gone. Without a catalog every video row would claim
          // the meeting was deleted — while it loads, and permanently if the
          // fetch failed (offline on a cache miss) — and the Resume card would
          // skip past live videos to something older, flipping its answer under
          // the reader.
          if (videos.data == null) return { state: "unknown" };
          const video = videos.data.find((row) => row.id === visit.targetId);
          if (video == null) return { state: "gone" };
          return ready(
            meetingDeepLink(
              roundId,
              video.id,
              video.track,
              video.type,
              video.homeRecommenderId,
            ),
          );
        }
        default:
          return { state: "gone" };
      }
    },
    [roundHref, roundId, videos.data, activeRecommenderIds, activeProposalIds],
  );

  /**
   * The card at the top of History: the MOST RECENT place, i.e. the first row
   * of the list below it.
   *
   * It used to show the rail's bookmark, which only advanced when you launched
   * from the rail — so after any browsing the card and the list disagreed, on a
   * tab that is sorted by recency. "Where you left off" has to mean the same
   * thing as "the top of this list" or one of them is lying.
   *
   * A video borrows its rail step for the thumbnail and remaining time; other
   * kinds just say what they are.
   */
  const resumeVisit = useMemo(() => {
    for (const visit of visits ?? []) {
      const target = resolveTarget(visit);
      // An unresolvable-YET row stops the scan rather than being skipped: the
      // card would otherwise answer with something older and then change its
      // mind once the catalog landed. A row that is genuinely gone is skipped.
      if (target.state === "unknown") return null;
      if (target.state === "gone") continue;
      const href = target.href;
      const step =
        visit.kind === "video"
          ? (flattenTodoSteps(rail?.steps ?? []).find(
              (candidate) => candidate.video?.videoId === visit.targetId,
            ) ?? null)
          : null;
      return {
        visit,
        href,
        step,
        detail: step?.progress.detail ?? visitKindLabels[visit.kind],
      };
    }
    return null;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- resolveTarget is rebuilt each render; its inputs are the deps below.
  }, [visits, rail, resolveTarget]);

  return (
    <Tabs defaultValue="todo" className="flex min-h-0 flex-1 flex-col gap-4">
      <TabsList className="mx-6 w-[calc(100%-3rem)]">
        <TabsTrigger value="todo">To Do</TabsTrigger>
        <TabsTrigger value="history">
          History{" "}
          {history.data != null && history.data.visits.length > 0
            ? `(${history.data.visits.length})`
            : ""}
        </TabsTrigger>
      </TabsList>

      <TabsContent
        value="todo"
        className="flex min-h-0 flex-1 flex-col gap-4 data-[state=inactive]:hidden"
      >
        {rail != null && (
          <div className="flex items-center gap-3 px-6">
            <TodoProgressRing fraction={rail.fraction} />
            <div className="flex min-w-0 flex-col">
              <span className="text-body-sm font-medium">
                Round progress · {Math.round(rail.fraction * 100)}%
              </span>
              <span className="text-body-sm tabular-nums text-text-muted">
                {rail.doneCount} of {rail.requiredCount} steps done
              </span>
            </div>
          </div>
        )}

        <TodoDrawerBody
          rail={rail}
          isLoading={isLoading}
          roundId={roundId}
          onNavigate={onNavigate}
          expanded={expanded}
          setExpanded={setExpanded}
        />
      </TabsContent>

      <TabsContent
        value="history"
        // Same reserved gutter as the To Do tab's list, so switching tabs
        // doesn't shift the content sideways either.
        className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto pb-6 data-[state=inactive]:hidden [scrollbar-gutter:stable]"
      >
        {resumeVisit != null && (
          <TodoResumeCard
            title={resumeVisit.visit.label}
            detail={resumeVisit.detail}
            step={resumeVisit.step}
            eyebrow="Pick up where you left off"
            action={resumeVisit.step?.video != null ? "Resume" : "Open"}
            onOpen={() => onNavigate(resumeVisit.href)}
          />
        )}

        <TodoHistoryList
          visits={visits}
          isLoading={history.isLoading}
          now={now}
          resolveTarget={resolveTarget}
          onOpen={(_visit, href) => onNavigate(href)}
        />
      </TabsContent>
    </Tabs>
  );
}
